import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import "../Styles/communitymember.css";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const API_BASE_URL = "";

// Fields shown in the Add / Edit / View modals (same order as the join form)
const memberFields = [
  { name: "fullName", label: "Full Name", type: "text" },
  { name: "gender", label: "Gender", type: "text" },
  { name: "contact", label: "Contact Number", type: "text" },
  { name: "personalEmail", label: "Personal Email", type: "email" },
  { name: "officialEmail", label: "Official Email", type: "email" },
  { name: "linkedIn", label: "LinkedIn Profile URL", type: "text" },
  { name: "designation", label: "Current Designation", type: "text" },
  { name: "company", label: "Organization Name", type: "text" },
  { name: "industry", label: "Nature of Industry", type: "text" },
  { name: "experience", label: "Total Professional Experience", type: "text" },
  { name: "country", label: "Country", type: "text" },
  { name: "membershipCategory", label: "Membership Category", type: "text" },
  { name: "state", label: "State", type: "text" },
  { name: "city", label: "City", type: "text" },
  { name: "reason", label: "Primary Reason for Joining Vanakkam HRD", type: "text" },
  { name: "referralSource", label: "Referral Source", type: "text" },
];

const emptyForm = () =>
  Object.fromEntries(memberFields.map((f) => [f.name, ""]));

// ---------------------------------------------------------------
// TEMPORARY: dummy data for local frontend testing only.
// It shows ONLY on localhost when the API fails. Delete this block
// (and the "LOCAL DUMMY" block inside fetchMembers) when done.
// ---------------------------------------------------------------
const IS_LOCAL =
  typeof window !== "undefined" &&
  ["localhost", "127.0.0.1"].includes(window.location.hostname);

const DUMMY_MEMBERS = [
  {
    id: 1,
    membershipId: "VHRD-CM-0001",
    fullName: "Praveen Kumar",
    gender: "male",
    countryCode: "+91",
    contact: "9876543210",
    personalEmail: "praveen.personal@example.com",
    officialEmail: "praveen@company.com",
    linkedIn: "https://www.linkedin.com/in/example",
    imagePath: "",
    designation: "HR Manager",
    company: "Vanakkam Technologies",
    industry: "Information Technology / IT Services",
    experience: "6 - 10 years",
    country: "India",
    state: "Tamil Nadu",
    city: "Chennai",
    membershipCategory: "Elite HR Circle",
    reason: "HR Networking & Community",
    referralSource: "LinkedIn",
    receiveUpdates: 1,
    accuracyConfirmed: 1,
    privacyAccepted: 1,
    communicationConsent: 1,
    status: "pending",
  },
];

const formatMembershipId = (m) =>
  m.membershipId || `VHRD${String(m.id).padStart(4, "0")}`;

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// ---- Dropdown options for the Edit page (same lists as the join form) ----
const INDUSTRY_OPTIONS = [
  "Information Technology / IT Services", "Manufacturing",
  "Banking, Financial Services & Insurance", "Healthcare & Pharmaceuticals",
  "Education", "Consulting & Professional Services", "Retail & E-commerce",
  "Automotive", "GCC / Shared Services", "Staffing & Recruitment",
  "Agriculture & AgriTech", "Logistics & Supply Chain", "Hospitality & Tourism",
  "NGO / Non-Profit", "Food & Beverage", "Chemicals", "Oil & Gas",
  "Construction & Real Estate", "Government / Public Sector", "Other",
];
const STATE_OPTIONS = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal", "Andaman and Nicobar Islands",
  "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu", "Delhi",
  "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];
const COUNTRY_CODES = ["+91", "+1", "+44", "+971", "+65", "+61"];
const EXPERIENCE_OPTIONS = [
  "0 - 2 years", "3 - 5 years", "6 - 10 years", "11 - 15 years", "16 - 20 years", "20+ years",
];
const MEMBERSHIP_CATEGORY_OPTIONS = [
  "Elite HR Circle", "Future Leaders Club", "Educational Club", "Founders Club",
  "Consultants & Advisory Club",
];
const REASON_OPTIONS = [
  "HR Networking & Community", "HR Summits & Conferences",
  "CHRO Roundtables & Leadership Forums",
  "HR Certifications & Professional Development", "HR Awards & Recognition",
  "HR Technology & AI", "Career Growth & Mentorship",
  "Speaking Opportunities & Thought Leadership",
  "Corporate Partnerships & Business Networking", "Campus & Industry Connect",
];
const REFERRAL_OPTIONS = [
  "LinkedIn", "Website", "HR Event / Conference", "Colleague / Friend",
  "Corporate Partner", "Other",
];

// Membership categories shown in the "Members" dropdown (name + short code)
const CATEGORY_META = [
  { name: "Elite HR Circle", code: "EHC" },
  { name: "Future Leaders Club", code: "FLC" },
  { name: "Educational Club", code: "EDU" },
  { name: "Founders Club", code: "FND" },
  { name: "Consultants & Advisory Club", code: "CON" },
];

// If the saved value is not in the list (older data), still show it as an option
const withCurrent = (list, value) =>
  value && !list.includes(value) ? [value, ...list] : list;

// Grey helper text shown under the Membership Category value
const MEMBERSHIP_CATEGORY_HINTS = {
  "Elite HR Circle": "(For CHROs, HR Heads, HR Directors, VPs and senior HR leaders)",
  "Future Leaders Club": "(For emerging HR professionals, young leaders and aspiring HR managers)",
  "Educational Club": "(For HR faculty, academicians, students and campus representatives)",
  "Founders Club": "(For startup founders, entrepreneurs and business owners)",
  "Consultants & Advisory Club": "(For HR consultants, advisors, coaches and independent practitioners)",
};

// One read-only field in the View page (label + value box)
function ViewField({ label, value, hint }) {
  return (
    <div className="cml-view-field">
      <label className="cml-view-label">
        {label}
        <span className="cml-view-req"> *</span>
      </label>
      <div className="cml-view-input">{value || "—"}</div>
      {hint && <p className="cml-view-hint">{hint}</p>}
    </div>
  );
}

// Read-only checkbox row (Information Accuracy, Privacy Policy, Consent)
function ViewCheck({ title, text, checked }) {
  return (
    <div className="cml-view-field">
      <label className="cml-view-label">
        {title}
        <span className="cml-view-req"> *</span>
      </label>
      <div className="cml-view-check">
        <span className={`cml-view-box${checked ? " on" : ""}`}>
          {checked && <Check size={12} strokeWidth={3.5} />}
        </span>
        <span className="cml-view-check-text">{text}</span>
      </div>
    </div>
  );
}

// ---------- Edit page building blocks ----------
function EditField({ label, hint, children }) {
  return (
    <div className="cml-view-field">
      <label className="cml-view-label">
        {label}
        <span className="cml-view-req"> *</span>
      </label>
      {children}
      {hint && <p className="cml-view-hint">{hint}</p>}
    </div>
  );
}

function EditInput({ name, value, onChange, type = "text", placeholder }) {
  return (
    <input
      className="cml-edit-input"
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      placeholder={placeholder}
      required
    />
  );
}

function EditSelect({ name, value, onChange, options, placeholder }) {
  return (
    <select
      className={`cml-edit-input cml-edit-select${value ? "" : " is-empty"}`}
      name={name}
      value={value || ""}
      onChange={onChange}
      required
    >
      <option value="" disabled>{placeholder}</option>
      {withCurrent(options, value).map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function EditCheck({ title, text, checked, onChange }) {
  return (
    <div className="cml-view-field">
      <label className="cml-view-label">
        {title}
        <span className="cml-view-req"> *</span>
      </label>
      <label className="cml-view-check cml-edit-click">
        <input
          type="checkbox"
          className="cml-edit-hidden"
          checked={checked}
          onChange={onChange}
        />
        <span className={`cml-view-box${checked ? " on" : ""}`}>
          {checked && <Check size={12} strokeWidth={3.5} />}
        </span>
        <span className="cml-view-check-text">{text}</span>
      </label>
    </div>
  );
}

// `onViewChange` is optional: the parent layout can pass it to change the
// top-bar title. It receives "view", "edit" or false.
export default function CommunityMembersList({ onViewChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [query, setQuery] = useState("");

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalMembers, setTotalMembers] = useState(0);
  const [totalApproved, setTotalApproved] = useState(0);
  const [totalRejected, setTotalRejected] = useState(0);

  // Count of members per category (the five boxes)
  const [categoryCounts, setCategoryCounts] = useState({});

  const [addingOpen, setAddingOpen] = useState(false);
  const [addForm, setAddForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);

  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [viewingMember, setViewingMember] = useState(null);

  // Debounce search input so we're not hitting the DB on every keystroke
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  // Reset to page 1 whenever the page size or search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, debouncedQuery]);

  // Jump to the top when the View / Edit page opens, and tell the parent
  // so it can change the top-bar title.
  useEffect(() => {
    if (viewingMember || editingMember) window.scrollTo({ top: 0 });
    if (onViewChange) {
      onViewChange(editingMember ? "edit" : viewingMember ? "view" : false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingMember, editingMember]);

  // Reset the title when leaving the Community Members page
  useEffect(() => {
    return () => {
      if (onViewChange) onViewChange(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
      });
      if (debouncedQuery) params.set("search", debouncedQuery);

      const res = await fetch(`${API_BASE_URL}/api/community-members?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to load community members");
      const data = await res.json();

      setMembers(data.members || []);
      setTotalPages(data.totalPages || 1);
      setTotalMembers(data.total || 0);
      setTotalApproved(data.totalApproved || 0);
      setTotalRejected(data.totalRejected || 0);
      setCategoryCounts(data.categoryCounts || {});
      setSelected([]);
      setSelectAll(false);
      setError(null);
    } catch (err) {
      console.error(err);

      // ===== LOCAL DUMMY (remove later) =====
      if (IS_LOCAL) {
        setMembers(DUMMY_MEMBERS);
        setTotalPages(1);
        setTotalMembers(DUMMY_MEMBERS.length);
        setTotalApproved(0);
        setTotalRejected(0);
        setCategoryCounts({ "Elite HR Circle": DUMMY_MEMBERS.length });
        setError(null);
        return;
      }
      // ===== END LOCAL DUMMY =====

      setMembers([]);
      setError("Failed to load community members. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, debouncedQuery]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    let start = Math.max(2, currentPage - 1);
    let end = Math.min(totalPages - 1, currentPage + 1);
    if (start > 2) pages.push("dots-start");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("dots-end");
    pages.push(totalPages);
    return pages;
  };

  const toggleAll = () => {
    setSelectAll((v) => !v);
    setSelected(selectAll ? [] : members.map((m) => m.id));
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ---- Delete ----
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Community Member?",
      text: "Are you sure you want to delete this member? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e0432c",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/community-members/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete");
      await fetchMembers();
      Swal.fire({
        title: "Deleted!",
        text: "The member has been deleted.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: "Failed to delete community member. Please try again.",
        icon: "error",
      });
    }
  };
  // ---- Bulk delete ----
const handleBulkDelete = async () => {
  if (selected.length === 0) {
  Swal.fire({
    icon: "info",
    title: "No Members Selected",
    text: "Please select at least one member to delete.",
  });
  return;
}

  const result = await Swal.fire({
    title: `Delete ${selected.length} Member${selected.length > 1 ? "s" : ""}?`,
    text: "Are you sure you want to delete all selected members? This action cannot be undone.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#e0432c",
    cancelButtonColor: "#6c757d",
    confirmButtonText: "Yes, Delete All",
    cancelButtonText: "No",
  });
  if (!result.isConfirmed) return;

  try {
    const res = await fetch(`${API_BASE_URL}/api/community-members/bulk-delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids: selected }),
    });
    if (!res.ok) throw new Error("Failed to delete");
    await fetchMembers(); // also clears the selection
    Swal.fire({
      title: "Deleted!",
      text: "The selected members have been deleted.",
      icon: "success",
      timer: 1500,
      showConfirmButton: false,
    });
  } catch (err) {
    console.error(err);
    Swal.fire({
      title: "Error",
      text: "Failed to delete selected members. Please try again.",
      icon: "error",
    });
  }
};

  // ---- Bulk approve (same as the single Approve button, for all selected members) ----
  const handleBulkApprove = async () => {
    if (selected.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Members Selected",
        text: "Please select at least one member to approve.",
      });
      return;
    }

    const result = await Swal.fire({
      title: `Approve ${selected.length} Member${selected.length > 1 ? "s" : ""}?`,
      text: "The approval email with the membership card will be sent to each member. Members already approved or rejected are skipped.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Approve All",
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;

    setBulkApproving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/community-members/bulk-approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: selected }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed to approve");

      await fetchMembers(); // also clears the selection

      const skipped = Number(data.skipped || 0);
      Swal.fire({
        title: "Approved!",
        html:
          `<p><b>${data.approved}</b> member${data.approved === 1 ? "" : "s"} approved.</p>` +
          (skipped
            ? `<p style="font-size:13px;color:#444">${skipped} skipped (already approved or rejected).</p>`
            : "") +
          (data.approved
            ? `<p style="font-size:13px;color:#444">Approval emails are being sent in the background (about 15 per minute).</p>`
            : ""),
        icon: data.approved ? "success" : "info",
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: err.message || "Failed to approve selected members. Please try again.",
        icon: "error",
      });
    } finally {
      setBulkApproving(false);
    }
  };

  // ---- Approve / Reject ----
  const handleStatusChange = async (id, status) => {
    const isApprove = status === "approved";
    const result = await Swal.fire({
      title: isApprove ? "Approve Member?" : "Reject Member?",
      text: isApprove
        ? "Are you sure you want to approve this member?"
        : "Are you sure you want to reject this member?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: isApprove ? "#16a34a" : "#e0432c",
      cancelButtonColor: "#6c757d",
      confirmButtonText: isApprove ? "Yes, Approve" : "Yes, Reject",
      cancelButtonText: "No",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/community-members/${id}/status`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        }
      );
      if (!res.ok) throw new Error("Failed to update status");
      await fetchMembers();
      Swal.fire({
        title: isApprove ? "Approved!" : "Rejected!",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: "Error",
        text: "Failed to update status. Please try again.",
        icon: "error",
      });
    }
  };

  // ---- Add new member ----
  const openAddModal = () => {
    setAddForm(emptyForm());
    setAddingOpen(true);
  };
  const closeAddModal = () => {
    setAddingOpen(false);
    setAddForm({});
  };
  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/community-members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(addForm),
      });
      if (!res.ok) throw new Error("Failed to add community member");
      setCurrentPage(1);
      await fetchMembers();
      closeAddModal();
    } catch (err) {
      console.error(err);
      alert("Failed to add community member. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---- Edit member (opens the Edit page) ----
  const openEditModal = (m) => {
    setEditingMember(m);
    setEditForm({
      fullName: m.fullName || "",
      gender: (m.gender || "").toLowerCase(),
      countryCode: m.countryCode || "+91",
      contact: m.contact || "",
      personalEmail: m.personalEmail || "",
      officialEmail: m.officialEmail || "",
      linkedIn: m.linkedIn || "",
      designation: m.designation || "",
      company: m.company || "",
      industry: m.industry || "",
      experience: m.experience || "",
      country: m.country || "",
      state: m.state || "",
      city: m.city || "",
      membershipCategory: m.membershipCategory || "",
      membershipId: formatMembershipId(m),
      reason: m.reason || "",
      referralSource: m.referralSource || "",
      receiveUpdates: Number(m.receiveUpdates) === 1 ? 1 : 0,
      accuracyConfirmed: Number(m.accuracyConfirmed) === 1 ? 1 : 0,
      privacyAccepted: Number(m.privacyAccepted) === 1 ? 1 : 0,
      communicationConsent: Number(m.communicationConsent) === 1 ? 1 : 0,
    });
  };
  const closeEditModal = () => {
    setEditingMember(null);
    setEditForm({});
  };
  const setEditField = (name, value) =>
    setEditForm((prev) => ({ ...prev, [name]: value }));
  const handleEditChange = (e) => setEditField(e.target.name, e.target.value);
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/community-members/${editingMember.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editForm),
        }
      );
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to update community member");
      }
      await fetchMembers();
      closeEditModal();
      Swal.fire({
        title: "Saved!",
        text: "The member details have been updated.",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save changes. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---- View member ----
  const openViewModal = (m) => setViewingMember(m);
  const closeViewModal = () => setViewingMember(null);

  // ---- Export to Excel ----
  const handleExportToExcel = () => {
    if (members.length === 0) {
      Swal.fire({
        icon: "info",
        title: "No Data",
        text: "There are no community members to export.",
      });
      return;
    }

    const excelData = members.map((m, index) => ({
      "S.No": (currentPage - 1) * pageSize + index + 1,
      "Membership ID": formatMembershipId(m),
      "Full Name": m.fullName || "",
      "Gender": m.gender || "",
      "Contact Number": m.contact || "",
      "Personal Email": m.personalEmail || "",
      "Official Email": m.officialEmail || "",
      "LinkedIn Profile URL": m.linkedIn || "",
      "Current Designation": m.designation || "",
      "Organization Name": m.company || "",
      "Nature of Industry": m.industry || "",
      "Total Professional Experience": m.experience || "",
      "State": m.state || "",
      "City": m.city || "",
      "Country": m.country || "",
      "Membership Category": m.membershipCategory || "",
      "Primary Reason for Joining Vanakkam HRD": m.reason || "",
      "Referral Source": m.referralSource || "",
      "Status": m.status || "pending",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Community Members");
    XLSX.writeFile(workbook, "community_members.xlsx");
  };

  // Footer ("Showing x-y of N" + pagination). Used by the list AND the View page.
  const renderFooter = (onPage) => (
    <div className="cml-table-footer">
      <span>
        Showing {members.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}
        &ndash;
        {(currentPage - 1) * pageSize + members.length} of {totalMembers} Members
      </span>
      <div className="cml-pagination">
        <button
          className="cml-page-btn"
          onClick={() => onPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft size={14} />
        </button>
        {getPageNumbers().map((p, i) =>
          p === "dots-start" || p === "dots-end" ? (
            <span key={`${p}-${i}`} className="cml-page-dots">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`cml-page-btn${p === currentPage ? " active" : ""}`}
              onClick={() => onPage(p)}
            >
              {p}
            </button>
          )
        )}
        <button
          className="cml-page-btn"
          onClick={() => onPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );

  // From the View page: clicking a page number goes back to the list on that page
  const goToPageFromView = (page) => {
    if (page < 1 || page > totalPages) return;
    setViewingMember(null);
    setCurrentPage(page);
  };

  // From the Edit page: clicking a page number goes back to the list on that page
  const goToPageFromEdit = (page) => {
    if (page < 1 || page > totalPages) return;
    closeEditModal();
    setCurrentPage(page);
  };

  return (
    <div className="cml-app">
      {/* ---------------- Main content ---------------- */}
      {!viewingMember && !editingMember && (
      <main className="cml-main">
        <section className="cml-panel">
          <div className="cml-controls">
            <label className="cml-show-entries">
              Show Entries :
              <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <span className="cml-pill cml-pill--total">Total Members : {totalMembers}</span>

            <span className="cml-pill cml-pill--approved">Total Approvals : {totalApproved}</span>

            <span className="cml-pill cml-pill--rejected">Total Rejected : {totalRejected}</span>

            <button className="cml-pill cml-pill--export" onClick={handleExportToExcel}>
              <span className="cml-excel-icon-wrap">
                <img
                  src="/assets/excel.png"
                  alt="Excel"
                  className="cml-excel-icon cml-excel-icon--default"
                />
                <img
                  src="/assets/excel-white.png"
                  alt="Excel"
                  className="cml-excel-icon cml-excel-icon--hover"
                />
              </span>
              Export To Excel
            </button>

            {/* <button className="cml-pill cml-pill--add" onClick={openAddModal}>
              <Plus size={14} />
              Add New Community Member
            </button> */}

            <div className="cml-search">
              <Search size={14} />
              <input
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>
          {/* Second row: one box per membership category with its count */}
          <div className="cml-cat-row">
            {CATEGORY_META.map((c) => (
              <span
                key={c.code}
                className={`cml-pill cml-pill--cat cml-pill--${c.code.toLowerCase()}`}
                title={c.name}
              >
                {c.code} : {Number(categoryCounts[c.name] || 0)}
              </span>
            ))}

            {/* Bulk approve + bulk delete - right next to the CON box (Bulk Delete last) */}
            <button
              className="cml-pill cml-pill--bulk"
              style={{ color: "#16a34a", borderColor: "#16a34a" }}
              onClick={handleBulkApprove}
              disabled={bulkApproving}
            >
              <Check size={14} />
              {bulkApproving
                ? "Approving..."
                : `Bulk Approve${selected.length > 0 ? ` (${selected.length})` : ""}`}
            </button>

            <button
              className="cml-pill cml-pill--bulk"
              onClick={handleBulkDelete}
            >
              <Trash2 size={14} />
              Bulk Delete{selected.length > 0 ? ` (${selected.length})` : ""}
            </button>
          </div>

          <div className="cml-table-outer">
            <div className="cml-table-inner">
              {loading ? (
                <p style={{ padding: "20px" }}>Loading community members...</p>
              ) : error ? (
                <p style={{ padding: "20px", color: "#e0432c" }}>{error}</p>
              ) : (
                <table className="cml-table">
                  <thead>
                    <tr>
                      <th className="cml-checkbox-col">
                        <label className="cml-select-all">
                          <input type="checkbox" checked={selectAll} onChange={toggleAll} />
                          <span>Select All</span>
                        </label>
                      </th>
                      <th>S.No</th>
                      <th>Membership ID</th>
                      <th>Full Name</th>
                      <th>Gender</th>
                      <th>Contact Number</th>
                      <th>Personal Email</th>
                      <th>Official Email</th>
                      <th>LinkedIn Profile URL</th>
                      <th>Current Designation</th>
                      <th>Organization Name</th>
                      <th>Nature of Industry</th>
                      <th>Total Professional Experience</th>
                      <th>State</th>
                      <th>City</th>
                      <th>Country</th>
                      <th>Membership Category</th>
                      <th>Primary Reason for Joining Vanakkam HRD</th>
                      <th>Referral Source</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.length === 0 ? (
                      <tr>
                        <td colSpan={21} style={{ padding: "20px", textAlign: "center" }}>
                          No community members yet.
                        </td>
                      </tr>
                    ) : (
                      members.map((m, index) => (
                        <tr key={m.id}>
                          <td className="cml-checkbox-col">
                            <input
                              type="checkbox"
                              checked={selected.includes(m.id)}
                              onChange={() => toggleOne(m.id)}
                            />
                          </td>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>{formatMembershipId(m)}</td>
                          <td>
                            <div className="cml-user-cell">
                              {m.imagePath ? (
                                <img
                                  src={`${API_BASE_URL}${m.imagePath}`}
                                  alt={m.fullName}
                                  className="cml-avatar cml-avatar--photo"
                                />
                              ) : (
                                <span className="cml-avatar">{m.fullName?.charAt(0)}</span>
                              )}
                              {m.fullName}
                            </div>
                          </td>
                          <td>{m.gender}</td>
                          <td>{m.contact}</td>
                          <td>{m.personalEmail}</td>
                          <td>{m.officialEmail}</td>
                          <td>
                            {m.linkedIn ? (
                              <a href={m.linkedIn} target="_blank" rel="noreferrer" className="cml-link">
                                View Profile
                              </a>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td>{m.designation}</td>
                          <td>{m.company}</td>
                          <td>{m.industry}</td>
                          <td>{m.experience}</td>
                          <td>{m.state}</td>
                          <td>{m.city}</td>
                          <td>{m.country}</td>
                          <td>{m.membershipCategory}</td>
                          <td>{m.reason}</td>
                          <td>{m.referralSource}</td>
                          <td>
                            <div className="cml-status-actions">
                              <button
                                className={`cml-status-btn cml-status-btn--approve${m.status === "approved" ? " is-active" : ""}${m.status === "rejected" ? " is-locked" : ""}`}
                                disabled={m.status === "approved" || m.status === "rejected"}
                                onClick={() => handleStatusChange(m.id, "approved")}
                              >
                                <Check size={13} />
                                {m.status === "approved" ? "Approved" : "Approve"}
                              </button>
                              <button
                                className={`cml-status-btn cml-status-btn--reject${m.status === "rejected" ? " is-active" : ""}${m.status === "approved" ? " is-locked" : ""}`}
                                disabled={m.status === "approved" || m.status === "rejected"}
                                onClick={() => handleStatusChange(m.id, "rejected")}
                              >
                                <X size={13} />
                                {m.status === "rejected" ? "Rejected" : "Reject"}
                              </button>
                            </div>
                          </td>
                          <td>
                            <div className="cml-row-actions">
                              <button
                                className="cml-icon-btn cml-icon-btn--view"
                                onClick={() => openViewModal(m)}
                              >
                                <img
                                  src="/assets/view.png"
                                  alt=""
                                  className="cml-btn-icon"
                                />
                                View
                              </button>
                              <button
                                className="cml-icon-btn cml-icon-btn--edit"
                                onClick={() => openEditModal(m)}
                              >
                                <Pencil size={13} />
                                Edit
                              </button>
                              <button
                                className="cml-icon-btn cml-icon-btn--delete"
                                onClick={() => handleDelete(m.id)}
                              >
                                <Trash2 size={13} />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {renderFooter(goToPage)}
        </section>
      </main>
      )}

      {/* ---------------- Add Modal ---------------- */}
      {addingOpen && (
        <div className="cml-modal-overlay" onClick={closeAddModal}>
          <div className="cml-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="cml-modal-title">Add New Community Member</h2>
            <form onSubmit={handleAddSubmit} className="cml-modal-form">
              {memberFields.map((f) => (
                <div className="cml-modal-field" key={f.name}>
                  <label>{f.label}</label>
                  <input
                    type={f.type}
                    name={f.name}
                    value={addForm[f.name] || ""}
                    onChange={handleAddChange}
                    required
                  />
                </div>
              ))}
              <div className="cml-modal-actions">
                <button type="button" className="cml-modal-btn cml-modal-btn--cancel" onClick={closeAddModal}>
                  Cancel
                </button>
                <button type="submit" className="cml-modal-btn cml-modal-btn--save" disabled={saving}>
                  {saving ? "Saving..." : "Add Member"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- View Modal ---------------- */}
      {viewingMember && (
        <main className="cml-main">
          <section className="cml-panel cml-view-page">
            {/* header */}
            <div className="cml-view-head">
              <h2 className="cml-view-title">
                Membership ID: <span>{formatMembershipId(viewingMember)}</span>
              </h2>
              <button
                type="button"
                className="cml-view-close"
                onClick={closeViewModal}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="cml-view-body">
              {/* ---------- 1 Personal Information (left, row 1) ---------- */}
              <div className="cml-view-section">
                <div className="cml-view-section-title">
                  <span className="cml-view-num">1</span>
                  Personal Information
                </div>
                <div className="cml-view-card">
                  <ViewField label="Full Name" value={viewingMember.fullName} />
                  <ViewField label="Gender" value={capitalize(viewingMember.gender)} />

                  <div className="cml-view-field">
                    <label className="cml-view-label">
                      Contact Number
                      <span className="cml-view-req"> *</span>
                    </label>
                    <div className="cml-view-input cml-view-phone">
                      <span>{viewingMember.countryCode || "+91"}</span>
                      <span className="cml-view-phone-divider" />
                      <span>{viewingMember.contact || "—"}</span>
                    </div>
                  </div>

                  <ViewField label="Personal Email ID" value={viewingMember.personalEmail} />
                  <ViewField label="Official Email ID" value={viewingMember.officialEmail} />
                  <ViewField label="LinkedIn Profile URL" value={viewingMember.linkedIn} />

                  <div className="cml-view-field">
                    <label className="cml-view-label">
                      Upload Your Image
                      <span className="cml-view-req"> *</span>
                    </label>
                    <div className="cml-view-upload">
                      <div className="cml-view-upload-left">
                        {viewingMember.imagePath ? (
                          <img
                            src={`${API_BASE_URL}${viewingMember.imagePath}`}
                            alt=""
                            className="cml-view-thumb"
                          />
                        ) : (
                          <img
                            src="/assets/jc_icn_05.png"
                            alt=""
                            className="cml-view-upload-icon"
                          />
                        )}
                        <div>
                          <p className="cml-view-upload-title">
                            {viewingMember.imagePath
                              ? viewingMember.imagePath.split("/").pop()
                              : "No image uploaded"}
                          </p>
                          <p className="cml-view-upload-sub">JPG or PNG · Maximum 10 MB</p>
                        </div>
                      </div>
                      {viewingMember.imagePath && (
                        <a
                          href={`${API_BASE_URL}${viewingMember.imagePath}`}
                          target="_blank"
                          rel="noreferrer"
                          className="cml-view-link"
                        >
                          View
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ---------- 2 Professional Information (right, row 1) ---------- */}
              <div className="cml-view-section">
                <div className="cml-view-section-title">
                  <span className="cml-view-num">2</span>
                  Professional Information
                </div>
                <div className="cml-view-card">
                  <ViewField label="Current Designation" value={viewingMember.designation} />
                  <ViewField label="Organization Name" value={viewingMember.company} />
                  <ViewField label="Nature of Industry" value={viewingMember.industry} />
                  <ViewField
                    label="Total Professional Experience"
                    value={viewingMember.experience}
                  />
                  <ViewField label="Membership ID" value={formatMembershipId(viewingMember)} />
                </div>
              </div>

              {/* ---------- 3 Location Details (left, row 2) ---------- */}
              <div className="cml-view-section">
                <div className="cml-view-section-title">
                  <span className="cml-view-num">3</span>
                  Location Details
                </div>
                <div className="cml-view-card">
                  <ViewField label="Country" value={viewingMember.country} />
                  <ViewField label="State" value={viewingMember.state} />
                  <ViewField label="City" value={viewingMember.city} />
                </div>
              </div>

              {/* ---------- 4 Membership Details (right, row 2) ---------- */}
              <div className="cml-view-section">
                <div className="cml-view-section-title">
                  <span className="cml-view-num">4</span>
                  Membership Details
                </div>
                <div className="cml-view-card">
                  <ViewField
                    label="Membership Category"
                    value={viewingMember.membershipCategory}
                    hint={MEMBERSHIP_CATEGORY_HINTS[viewingMember.membershipCategory]}
                  />
                  <ViewField
                    label="Primary Reason for Joining Vanakkam HRD"
                    value={viewingMember.reason}
                  />
                  <ViewField label="Referral Source" value={viewingMember.referralSource} />

                  <div className="cml-view-field">
                    <label className="cml-view-label">
                      Do You Wish to Receive Updates About Upcoming Events and Programs?
                      <span className="cml-view-req"> *</span>
                    </label>
                    <div className="cml-view-radio-row">
                      <span className="cml-view-radio">
                        <span
                          className={`cml-view-radio-dot${Number(viewingMember.receiveUpdates) === 1 ? " on" : ""}`}
                        />
                        Yes
                      </span>
                      <span className="cml-view-radio">
                        <span
                          className={`cml-view-radio-dot${Number(viewingMember.receiveUpdates) === 1 ? "" : " on"}`}
                        />
                        No
                      </span>
                    </div>
                  </div>

                  <ViewCheck
                    title="Information Accuracy"
                    text="I confirm that the information provided in this application is accurate and complete."
                    checked={Number(viewingMember.accuracyConfirmed) === 1}
                  />
                  <ViewCheck
                    title="Privacy Policy & Terms"
                    text="I have read and agree to the Vanakkam HRD Privacy Policy and Membership Terms."
                    checked={Number(viewingMember.privacyAccepted) === 1}
                  />
                  <ViewCheck
                    title="Communication Consent"
                    text="I agree to receive membership updates, event invitations and relevant professional communications from Vanakkam HRD."
                    checked={Number(viewingMember.communicationConsent) === 1}
                  />
                </div>
              </div>
            </div>

            {/* pagination at the bottom of the View page */}
            {renderFooter(goToPageFromView)}
          </section>
        </main>
      )}
      {/* ---------------- Edit Page (same layout as View, but editable) ---------------- */}
      {editingMember && (
        <main className="cml-main">
          <form className="cml-panel cml-view-page" onSubmit={handleEditSubmit}>
            {/* header */}
            <div className="cml-view-head">
              <h2 className="cml-view-title">
                Membership ID: <span>{formatMembershipId(editingMember)}</span>
              </h2>
              <button
                type="button"
                className="cml-view-close"
                onClick={closeEditModal}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="cml-view-body">
              {/* ---------- 1 Personal Information ---------- */}
              <div className="cml-view-section">
                <div className="cml-view-section-title">
                  <span className="cml-view-num">1</span>
                  Personal Information
                </div>
                <div className="cml-view-card">
                  <EditField label="Full Name">
                    <EditInput name="fullName" value={editForm.fullName} onChange={handleEditChange} />
                  </EditField>

                  <EditField label="Gender">
                    <select
                      className={`cml-edit-input cml-edit-select${editForm.gender ? "" : " is-empty"}`}
                      name="gender"
                      value={editForm.gender || ""}
                      onChange={handleEditChange}
                      required
                    >
                      <option value="" disabled>Select Gender</option>
                      {withCurrent(["male", "female"], editForm.gender).map((g) => (
                        <option key={g} value={g}>{capitalize(g)}</option>
                      ))}
                    </select>
                  </EditField>

                  <EditField label="Contact Number">
                    <div className="cml-edit-phone">
                      <select
                        className="cml-edit-code"
                        name="countryCode"
                        value={editForm.countryCode || "+91"}
                        onChange={handleEditChange}
                        aria-label="Country code"
                      >
                        {withCurrent(COUNTRY_CODES, editForm.countryCode).map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <span className="cml-view-phone-divider" />
                      <input
                        className="cml-edit-phone-input"
                        type="tel"
                        name="contact"
                        inputMode="numeric"
                        maxLength={15}
                        value={editForm.contact || ""}
                        onChange={(e) => setEditField("contact", e.target.value.replace(/\D/g, ""))}
                        required
                      />
                    </div>
                  </EditField>

                  <EditField label="Personal Email ID">
                    <EditInput type="email" name="personalEmail" value={editForm.personalEmail} onChange={handleEditChange} />
                  </EditField>
                  <EditField label="Official Email ID">
                    <EditInput type="email" name="officialEmail" value={editForm.officialEmail} onChange={handleEditChange} />
                  </EditField>
                  <EditField label="LinkedIn Profile URL">
                    <EditInput name="linkedIn" value={editForm.linkedIn} onChange={handleEditChange} />
                  </EditField>

                  <div className="cml-view-field">
                    <label className="cml-view-label">
                      Upload Your Image
                      <span className="cml-view-req"> *</span>
                    </label>
                    <div className="cml-view-upload">
                      <div className="cml-view-upload-left">
                        {editingMember.imagePath ? (
                          <img
                            src={`${API_BASE_URL}${editingMember.imagePath}`}
                            alt=""
                            className="cml-view-thumb"
                          />
                        ) : (
                          <img src="/assets/jc_icn_05.png" alt="" className="cml-view-upload-icon" />
                        )}
                        <div>
                          <p className="cml-view-upload-title">
                            {editingMember.imagePath
                              ? editingMember.imagePath.split("/").pop()
                              : "No image uploaded"}
                          </p>
                          <p className="cml-view-upload-sub">JPG or PNG · Maximum 10 MB</p>
                        </div>
                      </div>
                      {editingMember.imagePath && (
                        <a
                          href={`${API_BASE_URL}${editingMember.imagePath}`}
                          target="_blank"
                          rel="noreferrer"
                          className="cml-view-link"
                        >
                          View
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ---------- 2 Professional Information ---------- */}
              <div className="cml-view-section">
                <div className="cml-view-section-title">
                  <span className="cml-view-num">2</span>
                  Professional Information
                </div>
                <div className="cml-view-card">
                  <EditField label="Current Designation">
                    <EditInput name="designation" value={editForm.designation} onChange={handleEditChange} />
                  </EditField>
                  <EditField label="Organization Name">
                    <EditInput name="company" value={editForm.company} onChange={handleEditChange} />
                  </EditField>
                  <EditField label="Nature of Industry">
                    <EditSelect
                      name="industry"
                      value={editForm.industry}
                      onChange={handleEditChange}
                      options={INDUSTRY_OPTIONS}
                      placeholder="Select Your Nature of Industry"
                    />
                  </EditField>
                  <EditField label="Total Professional Experience">
                    <EditSelect
                      name="experience"
                      value={editForm.experience}
                      onChange={handleEditChange}
                      options={EXPERIENCE_OPTIONS}
                      placeholder="Select your Experience"
                    />
                  </EditField>
                  <EditField label="Membership ID">
                    <EditInput
                      name="membershipId"
                      value={editForm.membershipId}
                      onChange={handleEditChange}
                      placeholder="e.g. VHRD-EHC-0001"
                    />
                  </EditField>
                </div>
              </div>

              {/* ---------- 3 Location Details ---------- */}
              <div className="cml-view-section">
                <div className="cml-view-section-title">
                  <span className="cml-view-num">3</span>
                  Location Details
                </div>
                <div className="cml-view-card">
                  <EditField label="Country">
                    <EditInput name="country" value={editForm.country} onChange={handleEditChange} />
                  </EditField>
                  <EditField label="State">
                    {(editForm.country || "").trim().toLowerCase() === "india" ? (
                      <EditSelect
                        name="state"
                        value={editForm.state}
                        onChange={handleEditChange}
                        options={STATE_OPTIONS}
                        placeholder="Select your state"
                      />
                    ) : (
                      <EditInput name="state" value={editForm.state} onChange={handleEditChange} />
                    )}
                  </EditField>
                  <EditField label="City">
                    <EditInput name="city" value={editForm.city} onChange={handleEditChange} />
                  </EditField>
                </div>
              </div>

              {/* ---------- 4 Membership Details ---------- */}
              <div className="cml-view-section">
                <div className="cml-view-section-title">
                  <span className="cml-view-num">4</span>
                  Membership Details
                </div>
                <div className="cml-view-card">
                  <EditField
                    label="Membership Category"
                    hint={MEMBERSHIP_CATEGORY_HINTS[editForm.membershipCategory]}
                  >
                    <EditSelect
                      name="membershipCategory"
                      value={editForm.membershipCategory}
                      onChange={handleEditChange}
                      options={MEMBERSHIP_CATEGORY_OPTIONS}
                      placeholder="Select a membership category"
                    />
                  </EditField>
                  <EditField label="Primary Reason for Joining Vanakkam HRD">
                    <EditSelect
                      name="reason"
                      value={editForm.reason}
                      onChange={handleEditChange}
                      options={REASON_OPTIONS}
                      placeholder="Select a reason"
                    />
                  </EditField>
                  <EditField label="Referral Source">
                    <EditSelect
                      name="referralSource"
                      value={editForm.referralSource}
                      onChange={handleEditChange}
                      options={REFERRAL_OPTIONS}
                      placeholder="Select a source"
                    />
                  </EditField>

                  <div className="cml-view-field">
                    <label className="cml-view-label">
                      Do You Wish to Receive Updates About Upcoming Events and Programs?
                      <span className="cml-view-req"> *</span>
                    </label>
                    <div className="cml-view-radio-row">
                      <label className="cml-view-radio cml-edit-click">
                        <input
                          type="radio"
                          className="cml-edit-hidden"
                          name="receiveUpdates"
                          checked={editForm.receiveUpdates === 1}
                          onChange={() => setEditField("receiveUpdates", 1)}
                        />
                        <span className={`cml-view-radio-dot${editForm.receiveUpdates === 1 ? " on" : ""}`} />
                        Yes
                      </label>
                      <label className="cml-view-radio cml-edit-click">
                        <input
                          type="radio"
                          className="cml-edit-hidden"
                          name="receiveUpdates"
                          checked={editForm.receiveUpdates !== 1}
                          onChange={() => setEditField("receiveUpdates", 0)}
                        />
                        <span className={`cml-view-radio-dot${editForm.receiveUpdates !== 1 ? " on" : ""}`} />
                        No
                      </label>
                    </div>
                  </div>

                  <EditCheck
                    title="Information Accuracy"
                    text="I confirm that the information provided in this application is accurate and complete."
                    checked={editForm.accuracyConfirmed === 1}
                    onChange={(e) => setEditField("accuracyConfirmed", e.target.checked ? 1 : 0)}
                  />
                  <EditCheck
                    title="Privacy Policy & Terms"
                    text="I have read and agree to the Vanakkam HRD Privacy Policy and Membership Terms."
                    checked={editForm.privacyAccepted === 1}
                    onChange={(e) => setEditField("privacyAccepted", e.target.checked ? 1 : 0)}
                  />
                  <EditCheck
                    title="Communication Consent"
                    text="I agree to receive membership updates, event invitations and relevant professional communications from Vanakkam HRD."
                    checked={editForm.communicationConsent === 1}
                    onChange={(e) => setEditField("communicationConsent", e.target.checked ? 1 : 0)}
                  />
                </div>
              </div>

              {/* Save / Cancel - scroll with the form, at the end of the details */}
              <div className="cml-edit-actions">
                <button type="submit" className="cml-edit-save" disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
                <button type="button" className="cml-edit-cancel" onClick={closeEditModal}>
                  Cancel
                </button>
              </div>
            </div>

            {/* pagination at the bottom (fixed) */}
            {renderFooter(goToPageFromEdit)}
          </form>
        </main>
      )}
    </div>
  );
}